import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, eur, frDate } from "@/lib/api";

export default function TaxTab() {
  const year = new Date().getFullYear();
  const [start, setStart] = useState(`${year}-01-01`);
  const [end, setEnd] = useState(`${year}-12-31`);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/tourist-tax", { params: { start, end } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const { data: csv } = await api.get("/admin/tourist-tax.csv", { params: { start, end } });
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `taxe-de-sejour_${start}_${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  return (
    <div data-testid="tax-tab">
      <div className="flex flex-wrap items-end gap-4 border border-white/10 bg-panel p-5">
        <label className="text-sm">
          <span className="mb-1.5 block text-xs font-medium text-bone">Du</span>
          <input type="date" className="field !py-2" data-testid="tax-start" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="text-sm">
          <span className="mb-1.5 block text-xs font-medium text-bone">Au</span>
          <input type="date" className="field !py-2" data-testid="tax-end" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <button onClick={download} className="btn-primary" data-testid="tax-export-btn">
          <Download size={15} /> Export déclaration (CSV)
        </button>
      </div>

      {data && (
        <>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            <div className="border border-white/10 bg-panel p-5">
              <p className="font-display text-3xl text-bone" data-testid="tax-total-amount">{eur(data.total_amount)}</p>
              <p className="mt-1 text-xs text-muted-foreground">Taxe collectée sur la période</p>
            </div>
            <div className="border border-white/10 bg-panel p-5">
              <p className="font-display text-3xl text-bone" data-testid="tax-total-nights">{data.total_nights}</p>
              <p className="mt-1 text-xs text-muted-foreground">Nuitées assujetties</p>
            </div>
            <div className="border border-white/10 bg-panel p-5">
              <p className="font-display text-3xl text-bone">{data.rate.toFixed(2).replace(".", ",")} €</p>
              <p className="mt-1 text-xs text-muted-foreground">Tarif 4★ / pers. / nuit</p>
            </div>
          </div>

          <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
            Collectée pour {data.collector}. Le versement se fait par déclaration sur{" "}
            <a href={data.portal} target="_blank" rel="noreferrer" className="text-brass underline">{data.portal}</a>{" "}
            — aucun organisme de taxe de séjour n'accepte de virement automatique depuis Stripe.
          </p>

          <div className="mt-5 overflow-x-auto border border-white/10 bg-panel">
            <table className="w-full min-w-[620px] text-left text-sm whitespace-nowrap">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["Client", "Gîte", "Séjour", "Assujettis", "Nuitées", "Montant"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/10" data-testid={`tax-row-${r.id}`}>
                    <td className="px-4 py-3 text-bone">{r.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.gite_slug}</td>
                    <td className="px-4 py-3 text-muted-foreground">{frDate(r.start_date)} → {frDate(r.end_date)}</td>
                    <td className="px-4 py-3">{r.taxable_guests}</td>
                    <td className="px-4 py-3">{r.nights_sold}</td>
                    <td className="px-4 py-3 font-medium">{eur(r.amount)}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Aucun séjour payé sur cette période.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
